import * as Y from 'yjs'

import {
    applyEmbedPatch,
    applyTextDelta,
    clipObserveDeltaToParagraph,
    MARKER_KINDS,
    planParagraphMerge,
    planParagraphSplit,
    rejoinUtterances,
    ROOT_KEYS,
    syncProjection,
    translateSlateTextOp,
} from 'src/models/crdtV2'
import type {
    ApplyResult,
    EmbedFieldPatch,
    IndexedStream,
    ObserveDelta,
    ParagraphChangeMarker,
    ProjectionIndex,
    Qac,
    TimingsMap,
} from 'src/models/crdtV2'
import { Language } from 'src/models/Language'
import { BaseTranscriptClient, type TranscriptClient } from 'src/models/TranscriptClient'
import type { TranscriptMergeParagraphOp, TranscriptOp, TranscriptSplitParagraphOp } from 'src/models/TranscriptOperation'
import { BaseTranscriptUserStateManager, TranscriptUserState, TranscriptUserStateSelection } from 'src/models/TranscriptUserState'
import { TranscriptConnectionErrorType, TranscriptConnectionStateManager } from 'src/models/TranscriptConnectionState'
import { debug } from 'src/services/DebugService'
import { logCookieSize } from 'src/utils/cookies'
import { TranscriptDocCache } from 'src/services/TranscriptDocCache'
import type { TranscriptDelta } from 'src/models/TranscriptDelta'

import {
    ReadOnlyTranscriptError,
    TranscriptCrdtTransport,
    TranscriptCrdtTransportFatalError,
    TranscriptCrdtTransportStatus,
} from './TranscriptCrdtTransport'
import { paragraphChangePatchFromMapEvent } from './rtBrokerV2/paragraphChangePatch'
import { bindSnapshot } from './rtBrokerV2/snapshot'
import { applyParagraphMerge, applyParagraphSplit } from './rtBrokerV2/paragraphStructure'
import { applyXmlTextEdits, LOCAL_EDITOR_ORIGIN } from './rtBrokerV2/xmlTextEdits'
import {
    readUtteranceTiming,
    recordingStartedAtFromYArray,
    speakersFromYMap,
    xmlTextToIndexedStream,
    yTextEventDeltaToObserveDelta,
} from './rtBrokerV2/yjsAdapter'

/** Changes accumulated during one Yjs transaction (`doc["t"]` + `doc["timings"]`), flushed on `afterTransaction`. */
type TransactionBuffer = { textDeltas: ObserveDelta[]; embedPatches: EmbedFieldPatch[]; timingUtteranceIds: Set<string> }

const emptyBuffer = (): TransactionBuffer => ({ textDeltas: [], embedPatches: [], timingUtteranceIds: new Set() })

const isCopiedParagraphMarker = (embed: { kind: string }): embed is ParagraphChangeMarker =>
    embed.kind === MARKER_KINDS.paragraphChange &&
    'id' in embed &&
    typeof embed.id === 'string' &&
    'speakerId' in embed &&
    typeof embed.speakerId === 'string'

const paragraphMarkerFields = (stream: IndexedStream, paragraphId: string): { speakerId: string; qac?: Qac } | null => {
    for (const op of stream) {
        if (typeof op.insert === 'string' || !isCopiedParagraphMarker(op.insert) || op.insert.id !== paragraphId) {
            continue
        }
        if ('qac' in op.insert) {
            return { speakerId: op.insert.speakerId, qac: op.insert.qac }
        }

        return { speakerId: op.insert.speakerId }
    }

    return null
}

const isV2WriteOp = (op: TranscriptOp): boolean =>
    op.type === 'paragraph_text' || op.type === 'split_paragraph' || op.type === 'merge_paragraph'

export type RemoteParagraphNotice = { kind: 'delta'; delta: TranscriptDelta<string>[] } | { kind: 'reset' }

type RtBrokerTranscriptClientV2Options = {
    transport: TranscriptCrdtTransport
    doc: Y.Doc
    language: Language
    cache?: TranscriptDocCache | null
    isReadOnly?: boolean
}

class RtBrokerTranscriptV2UserStateManager extends BaseTranscriptUserStateManager {
    private _localState: TranscriptUserState = TranscriptUserState.EMPTY

    init = () => {}

    getLocalState = () => this._localState
    getRemoteStates = () => []

    updateSelection = (selection: TranscriptUserStateSelection | null) => {
        this._localState = { selection }
        this.emit()
    }
}

export const isRtBrokerTranscriptClient = (client: TranscriptClient): client is RtBrokerTranscriptClientV2 =>
    client instanceof RtBrokerTranscriptClientV2

/**
 * CRDT schema v2 rt-broker client. Reads `doc["t"]` + sidecars via the projection instead of the
 * legacy `getMap("data")` shape, while preserving the `TranscriptClient` query/subscribe surface.
 *
 * `doc["t"]` changes drive the incremental projection (`applyTextDelta` / `applyEmbedPatch`),
 * coalesced per Yjs transaction. Sidecar `doc["timings"]` / `doc["speakers"]` /
 * `doc["recordingStartedAt"]` are still read in full each flush; timings-only writes go through
 * `rejoinUtterances`. `applyOps` writes `paragraph_text`, `split_paragraph`, and `merge_paragraph`
 * into `doc["t"]` when `isReadOnly` is false, and rejects every other op. No `Y.UndoManager` is attached.
 */
export class RtBrokerTranscriptClientV2 extends BaseTranscriptClient {
    readonly source = 'rt-broker' as const
    private readonly _doc: Y.Doc
    private readonly _fragment: Y.XmlFragment
    private readonly _timingsMap: Y.Map<unknown>
    private readonly _language: Language
    private readonly _transport: TranscriptCrdtTransport
    private readonly _cache: TranscriptDocCache | null
    private _connectionStateManager = new TranscriptConnectionStateManager()
    private _userStateManager = new RtBrokerTranscriptV2UserStateManager()
    private _index: ProjectionIndex
    // Held and updated incrementally from the timings observer so the flush never rebuilds the whole
    // `TimingsMap` (that per-flush O(total words) cost dominated the E2E benchmark).
    private _timings: TimingsMap = {}
    private _buffer: TransactionBuffer = emptyBuffer()
    private _remoteListeners = new Map<string, Set<(notice: RemoteParagraphNotice) => void>>()
    private _isObserving = false
    private _isDestroyed = false

    constructor(options: RtBrokerTranscriptClientV2Options) {
        super()

        this._doc = options.doc
        this._language = options.language
        this._transport = options.transport
        this._cache = options.cache ?? null
        this.setReadOnly(options.isReadOnly ?? true)
        this._index = syncProjection([], {}, this._language).index

        // Observe the `t` fragment (a root that exists from construction, unlike its `XmlText` child,
        // which the server creates on first sync). `observeDeep` surfaces both the text delta and
        // in-place `paragraph_change` `Y.Map` field edits. `afterTransaction` is the single flush
        // point so a transaction touching text + sidecars emits once, and so a hydrate applied
        // before `init()` still reaches subscribers (Y.js fires observers only on updates).
        this._fragment = this._doc.get(ROOT_KEYS.text, Y.XmlFragment) as Y.XmlFragment
        this._fragment.observeDeep(this._onStreamDeep)
        // `doc["timings"]` is a sidecar (separate root), so its writes never touch `doc["t"]`. Track
        // which utterance ids changed and re-join only those paragraphs (`rejoinUtterances`) rather
        // than re-parsing the stream. Speakers / recordingStartedAt need no observer — the
        // `afterTransaction` re-read + `bindSnapshot` diff already surface their changes.
        this._timingsMap = this._doc.get(ROOT_KEYS.timings, Y.Map) as Y.Map<unknown>
        this._timingsMap.observeDeep(this._onTimingsDeep)
        this._doc.on('afterTransaction', this._onAfterTransaction)
        this._isObserving = true
    }

    /** Non-mutating read of the root `t` fragment's `XmlText` child; null before the first sync. */
    private _resolveStreamText = (): Y.XmlText | null => {
        const first = this._fragment.length > 0 ? this._fragment.get(0) : undefined

        return first instanceof Y.XmlText ? first : null
    }

    private _onStreamDeep = (events: Y.YEvent<Y.AbstractType<unknown>>[]) => {
        const streamText = this._resolveStreamText()

        for (const event of events) {
            if (event instanceof Y.YMapEvent) {
                const patch = paragraphChangePatchFromMapEvent(event)
                if (patch !== null) {
                    this._buffer.embedPatches.push(patch)
                }
                continue
            }

            // A `delta` on the stream `XmlText` is a text/embed structural change. The fragment's own
            // child-insert event (on first sync) targets the fragment, not the text, and is ignored —
            // the empty-index branch in `_flush` full-parses that initial content.
            if (event.target === streamText && 'delta' in event) {
                this._buffer.textDeltas.push(
                    yTextEventDeltaToObserveDelta(event.delta as Parameters<typeof yTextEventDeltaToObserveDelta>[0]),
                )
            }
        }
    }

    private _onTimingsDeep = (events: Y.YEvent<Y.AbstractType<unknown>>[]) => {
        const touched = new Set<string>()

        for (const event of events) {
            if (event.target === this._timingsMap && event instanceof Y.YMapEvent) {
                // set / delete of an utterance key.
                event.keysChanged.forEach((utteranceId) => touched.add(utteranceId))
            } else {
                // Mutation of an existing utterance's timing array: its key is the first path segment.
                const utteranceId = event.path[0]
                if (typeof utteranceId === 'string') {
                    touched.add(utteranceId)
                }
            }
        }

        // Keep the held `TimingsMap` current for only the changed utterances (no full rebuild), and
        // record them so the flush re-joins exactly those paragraphs.
        for (const utteranceId of touched) {
            const pairs = readUtteranceTiming(this._timingsMap, utteranceId)
            if (pairs === null) {
                delete this._timings[utteranceId]
            } else {
                this._timings[utteranceId] = pairs
            }
            this._buffer.timingUtteranceIds.add(utteranceId)
        }
    }

    private _onAfterTransaction = (transaction: { origin: unknown }): void => {
        this._flush(transaction.origin)
    }

    private _collectRemoteClips = (delta: ObserveDelta, clips: Map<string, TranscriptDelta<string>[]>) => {
        for (const paragraphId of this._remoteListeners.keys()) {
            const piece = clipObserveDeltaToParagraph(this._index.stream, paragraphId, delta)
            if (piece.length === 0) {
                continue
            }

            const existing = clips.get(paragraphId)
            if (existing === undefined) {
                clips.set(paragraphId, [...piece])
            } else {
                existing.push(...piece)
            }
        }
    }

    private _flush = (origin: unknown) => {
        if (this._isDestroyed) return

        const buffer = this._buffer
        this._buffer = emptyBuffer()

        const text = this._resolveStreamText()
        const clips = new Map<string, TranscriptDelta<string>[]>()
        const deliverRemote = this._remoteListeners.size > 0 && origin !== LOCAL_EDITOR_ORIGIN
        const onTextDelta = deliverRemote ? (delta: ObserveDelta) => this._collectRemoteClips(delta, clips) : undefined
        const { dirtyIds, addedIds, removedIds, fellBack } = this._applyBufferedChanges(buffer, text, onTextDelta)

        const { transcript, events } = bindSnapshot(this._transcript, {
            paragraphs: this._index.paragraphs,
            dirtyIds,
            addedIds,
            removedIds,
            speakers: speakersFromYMap(this._doc.get(ROOT_KEYS.speakers, Y.Map) as Y.Map<unknown>),
            recordingStartedAt: recordingStartedAtFromYArray(this._doc.get(ROOT_KEYS.recordingStartedAt, Y.Array) as Y.Array<unknown>),
        })

        this._transcript = transcript
        debug.log('RtBrokerTranscriptClientV2 observer', this._transcript, events)
        this.emitEvents(events)
        this._deliverRemote(deliverRemote, fellBack, dirtyIds, clips)
    }

    /**
     * Fold one transaction's buffered `doc["t"]` deltas / embed patches / timings changes into
     * `this._index`, returning the paragraph ids to re-bind. A localization failure (or initial
     * content) falls back to a full parse, which widens the dirty set to every paragraph.
     */
    private _applyBufferedChanges = (
        buffer: TransactionBuffer,
        text: Y.XmlText | null,
        onTextDelta?: (delta: ObserveDelta) => void,
    ): { dirtyIds: string[]; addedIds: string[]; removedIds: string[]; fellBack: boolean } => {
        const timings = this._timings
        const dirty = new Set<string>()
        const added = new Set<string>()
        const removed = new Set<string>()
        let fellBack = false

        const fullParse = () => {
            const stream = text === null ? [] : xmlTextToIndexedStream(text)
            this._index = syncProjection(stream, timings, this._language).index
            fellBack = true
        }

        const collect = (result: ApplyResult) => {
            result.dirtyIds.forEach((id) => dirty.add(id))
            result.addedIds.forEach((id) => {
                added.add(id)
                dirty.add(id)
            })
            result.removedIds.forEach((id) => removed.add(id))
        }

        // Initial content arrives without a usable text delta (fragment child-insert), so a
        // still-empty index with a populated stream is a full parse.
        const needsInitialParse = this._index.stream.length === 0 && text !== null && text.length > 0

        if (needsInitialParse) {
            fullParse()
        } else {
            for (const delta of buffer.textDeltas) {
                onTextDelta?.(delta)
                const result = applyTextDelta(this._index, delta, timings, this._language)
                this._index = result.index
                if (result.localizationFailed) {
                    fullParse()
                    break
                }
                // `usedFullParse` re-parsed this index's cloned stream. Keep that result and still apply
                // embed patches: a live re-read would copy a non-string id into the snapshot, and skipping
                // the patches would drop a valid in-place rename whose old id is still on the clone.
                collect(result)
            }

            if (!fellBack) {
                for (const patch of buffer.embedPatches) {
                    const result = applyEmbedPatch(this._index, patch, timings, this._language)
                    this._index = result.index
                    if (result.localizationFailed) {
                        fullParse()
                        break
                    }
                    collect(result)
                }
            }

            // Timings-only sidecar writes (and the timings half of a combined ASR finalize) actualize
            // word/paragraph times without a stream change. A full parse already re-joined everything.
            if (!fellBack && buffer.timingUtteranceIds.size > 0) {
                const result = rejoinUtterances(this._index, buffer.timingUtteranceIds, timings, this._language)
                this._index = result.index
                collect(result)
            }
        }

        if (fellBack) {
            return { dirtyIds: this._index.paragraphs.map((paragraph) => paragraph.id), addedIds: [], removedIds: [], fellBack }
        }

        return { dirtyIds: [...dirty], addedIds: [...added], removedIds: [...removed], fellBack }
    }

    private _onStatus = (status: TranscriptCrdtTransportStatus) => {
        debug.log('RtBrokerTranscriptClientV2._onStatus', status)
        this._connectionStateManager.setConnectionState({ status, error: null })
    }

    private _onSync = (isInitialized: boolean) => {
        debug.log('RtBrokerTranscriptClientV2._onSync', isInitialized)
        if (isInitialized) {
            this._connectionStateManager.setConnectionState({ isInitialized: true, isViewable: true })
        }
    }

    private _onFatal = (error: TranscriptConnectionErrorType) => {
        debug.log('RtBrokerTranscriptClientV2._onFatal', error)
        this._setFailedConnectionState(error)
    }

    // TODO(LG-1762): remove along with the transport `'terminated'` event when editing goes live.
    private _onTerminated = () => {
        debug.log('RtBrokerTranscriptClientV2._onTerminated')
        this._setTerminatedConnectionState()
    }

    public init = async () => {
        if (this._isDestroyed) return

        await this._hydrateFromCacheIfNeeded()

        if (this._isDestroyed) return

        logCookieSize()

        this._transport.on('status', this._onStatus)

        return new Promise<void>((resolve, reject) => {
            let settled = false

            const detachInitListeners = () => {
                this._transport.off('sync', onSyncDuringInit)
                this._transport.off('fatal', onFatalDuringInit)
                this._transport.off('terminated', onTerminatedDuringInit)
            }

            const installLongLivedListeners = () => {
                this._transport.on('sync', this._onSync)
                this._transport.on('fatal', this._onFatal)
                this._transport.on('terminated', this._onTerminated)
            }

            const onSyncDuringInit = (isInitialized: boolean) => {
                this._onSync(isInitialized)
                if (!isInitialized || settled) return

                settled = true
                detachInitListeners()
                installLongLivedListeners()
                resolve()
            }

            const onFatalDuringInit = (error: TranscriptConnectionErrorType) => {
                if (settled) return

                settled = true
                detachInitListeners()
                this._setFailedConnectionState(error)
                reject(new TranscriptCrdtTransportFatalError(error))
            }

            const onTerminatedDuringInit = () => {
                if (settled) return

                settled = true
                detachInitListeners()
                this._setTerminatedConnectionState()
                resolve()
            }

            this._transport.on('sync', onSyncDuringInit)
            this._transport.on('fatal', onFatalDuringInit)
            this._transport.on('terminated', onTerminatedDuringInit)

            this._transport.connect().catch((error) => {
                if (settled) return

                settled = true
                detachInitListeners()
                reject(error)
            })
        })
    }

    private _hydrateFromCacheIfNeeded = async (): Promise<void> => {
        if (!this._cache) return

        const hydrateResult = await this._cache.init()
        if (this._isDestroyed) return

        if (hydrateResult.hit) {
            this._connectionStateManager.setConnectionState({ isViewable: true })
        }
    }

    private _setFailedConnectionState = (error: TranscriptConnectionErrorType) => {
        this._connectionStateManager.setConnectionState({ status: 'failed', error })
    }

    // TODO(LG-1762): remove along with the transport `'terminated'` event when editing goes live.
    private _setTerminatedConnectionState = () => {
        this._connectionStateManager.setConnectionState({ status: 'terminated', isInitialized: true, error: null })
    }

    public destroy = () => {
        this._isDestroyed = true

        if (this._isObserving) {
            this._fragment.unobserveDeep(this._onStreamDeep)
            this._timingsMap.unobserveDeep(this._onTimingsDeep)
            this._doc.off('afterTransaction', this._onAfterTransaction)
            this._isObserving = false
        }

        this._transport.disconnect()
        void this._cache?.destroy()
    }

    getUserState = () => this._userStateManager
    getConnectionState = () => this._connectionStateManager

    subscribeRemoteParagraphText = (paragraphId: string, listener: (notice: RemoteParagraphNotice) => void): (() => void) => {
        const listeners = this._remoteListeners.get(paragraphId) ?? new Set()
        listeners.add(listener)
        this._remoteListeners.set(paragraphId, listeners)

        return () => {
            listeners.delete(listener)
            if (listeners.size === 0) {
                this._remoteListeners.delete(paragraphId)
            }
        }
    }

    private _deliverRemote = (
        enabled: boolean,
        fellBack: boolean,
        dirtyIds: readonly string[],
        clips: ReadonlyMap<string, TranscriptDelta<string>[]>,
    ) => {
        if (!enabled) {
            return
        }

        if (fellBack) {
            for (const paragraphId of dirtyIds) {
                this._notifyRemote(paragraphId, { kind: 'reset' })
            }

            return
        }

        for (const [paragraphId, delta] of clips) {
            this._notifyRemote(paragraphId, { kind: 'delta', delta })
        }
    }

    private _notifyRemote = (paragraphId: string, notice: RemoteParagraphNotice) => {
        const listeners = this._remoteListeners.get(paragraphId)
        if (listeners === undefined) {
            return
        }

        for (const listener of listeners) {
            listener(notice)
        }
    }

    /**
     * `paragraph_text`, `split_paragraph`, and `merge_paragraph` edit `doc["t"]`. Every other op is
     * the v1 nested-map write, which this client does not perform. A split or merge whose plan is
     * empty does not open a transaction. The local selection moves after the flush.
     */
    applyOps: TranscriptClient['applyOps'] = (ops) => {
        if (this.isReadOnly || ops.some((op) => !isV2WriteOp(op))) {
            throw new ReadOnlyTranscriptError()
        }

        for (const op of ops) {
            if (op.type === 'paragraph_text') {
                const text = this._resolveStreamText()
                if (text === null) {
                    continue
                }

                applyXmlTextEdits(text, translateSlateTextOp(xmlTextToIndexedStream(text), op.paragraphId, op.edit), LOCAL_EDITOR_ORIGIN)
                continue
            }

            if (op.type === 'split_paragraph') {
                this._applySplit(op)
                continue
            }

            if (op.type === 'merge_paragraph') {
                this._applyMerge(op)
            }
        }
    }

    private _applySplit = (op: TranscriptSplitParagraphOp): void => {
        const text = this._resolveStreamText()
        if (text === null) {
            return
        }

        const stream = xmlTextToIndexedStream(text)
        const plan = planParagraphSplit(stream, op.paragraphId, op.atIndex, op.newParagraphId)
        const source = paragraphMarkerFields(stream, op.paragraphId)
        if (plan === null || source === null) {
            return
        }

        applyParagraphSplit(text, plan, {
            id: op.newParagraphId,
            speakerId: source.speakerId,
            qac: source.qac,
        })
        this._userStateManager.updateSelection(plan.selection)
    }

    private _applyMerge = (op: TranscriptMergeParagraphOp): void => {
        const text = this._resolveStreamText()
        if (text === null) {
            return
        }

        const plan = planParagraphMerge(xmlTextToIndexedStream(text), op.paragraphId, op.direction)
        if (plan === null) {
            return
        }

        applyParagraphMerge(text, plan)
        this._userStateManager.updateSelection(plan.selection)
    }

    undo = () => {}
    redo = () => {}
}
